import {
  baseTicketCommentSchema,
  type GetTicketCommentsResponse,
} from "@mg/schemas/src/christo/catalyst";
import { Prohibit, Warning } from "@phosphor-icons/react";
import { Text } from "@radix-ui/themes";
import {
  HTMLContainer,
  Polygon2d,
  ShapeUtil,
  Vec,
  stopEventPropagation,
  type RecordProps,
  type TLBaseShape,
  T,
  type TLShapeId,
} from "@tldraw/tldraw";
import cx from "classnames";
import { useEffect, useMemo, useRef } from "react";

import {
  CANVAS_PIN_SIZE_PX,
  getCanvasPinAvatarLayout,
  getCanvasPinHitClipPath,
  getCanvasPinHitPolygonInBounds,
  isPointInCanvasPinHitBounds,
} from "./commentPinHitArea";

import { isNil } from "../../../../utils/fp";
import { useAppDispatch, useAppSelector } from "../../../../utils/hooks";
import { resolveAvatarColors } from "../../../../utils/resolveAvatarColors";
import { setActiveCommentId } from "../../../../utils/slices/ticket";
import { isCommentShapeVisible } from "../../../../utils/tldraw/comments";

export type TicketComment = GetTicketCommentsResponse[number];

type CommentShape = TLBaseShape<
  "comment",
  {
    w: number;
    h: number;
    commentId: unknown;
  }
>;

export class CommentAvatarShapeUtil extends ShapeUtil<CommentShape> {
  static override type = "comment-avatar" as const;

  static override props: RecordProps<CommentShape> = {
    w: T.number,
    h: T.number,
    commentId: T.unknown,
  };

  override isAspectRatioLocked = (_shape: CommentShape) => false;
  override canResize = (_shape: CommentShape) => false;
  override hideRotateHandle = (_shape: CommentShape) => true;
  override canEdit = () => true;

  getDefaultProps(): CommentShape["props"] {
    const { bounds } = getCanvasPinAvatarLayout(CANVAS_PIN_SIZE_PX);
    return {
      w: bounds.width,
      h: bounds.height,
      commentId: null,
    };
  }

  private isVisible(shape: CommentShape, userId?: string) {
    return isCommentShapeVisible(shape, userId, true);
  }

  getGeometry(_shape: CommentShape) {
    const zoomLevel = this.editor.getZoomLevel();
    const points = getCanvasPinHitPolygonInBounds(CANVAS_PIN_SIZE_PX).map(
      (p) => new Vec(p.x / zoomLevel, p.y / zoomLevel),
    );
    return new Polygon2d({
      points,
      isFilled: true,
    });
  }

  component = (shape: CommentShape) => {
    const zoomLevel = this.editor.getZoomLevel();

    // Slices
    const dispatch = useAppDispatch();
    const user = useAppSelector((state) => state.auth.value);
    const highlightedCommentId = useAppSelector(
      (state) => state.ticket.highlightedCommentId,
    );
    const activeCommentId = useAppSelector(
      (state) => state.ticket.activeCommentId,
    );
    const comments = useAppSelector((state) => state.ticket.comments);

    // Local state
    const comment = useMemo(() => {
      if (isNil(shape.meta) || isNil(shape.meta.comment)) return null;

      try {
        const commentObj = JSON.parse(shape.meta.comment as string);
        const comment = baseTicketCommentSchema.parse(commentObj);

        const existingComment = comments.find((c) => comment._id === c._id);

        if (existingComment) {
          return existingComment;
        }

        return comment;
      } catch {
        return null;
      }
    }, [shape.meta?.comment]);

    const isSelected = this.editor.getSelectedShapeIds().includes(shape.id);
    // Track selection so we only clear activeCommentId on real deselection.
    // Unselected pins must NOT wipe loader/deep-link active ids on mount
    // (ticketCommentRoute sets active before any pin is selected).
    const wasSelectedRef = useRef(false);

    // Effect chain

    useEffect(() => {
      if (isSelected) {
        this.editor.bringToFront([shape.meta.linkedCommentId as TLShapeId]);
        this.editor.setSelectedShapes([
          shape.id,
          shape.meta.linkedCommentId as TLShapeId,
        ]);
        if (!isNil(comment?._id)) {
          dispatch(setActiveCommentId(comment._id));
        }
        wasSelectedRef.current = true;
        return;
      }

      if (!wasSelectedRef.current) return;
      wasSelectedRef.current = false;
      // Only clear if this pin still owns the active id — a peer pin may
      // already have become active in the same selection change.
      if (!isNil(comment?._id) && activeCommentId === comment._id) {
        dispatch(setActiveCommentId(null));
      }
    }, [comment, isSelected, activeCommentId, dispatch]);

    /////
    // No hooks beyond this point
    /////

    if (!this.isVisible(shape, user?.userID)) {
      return (
        <div
          style={{
            width: 0,
            height: 0,
          }}
        />
      );
    }

    const isRequired = comment ? comment.isRequired : !!shape.meta?.isRequired;
    const isAI = comment?.isAI ?? false;

    function getInitials(name: string | undefined) {
      if (!name) return "?";
      const parts = name.trim().split(/\s+/);
      return parts
        .map((p) => p[0])
        .join("")
        .toUpperCase()
        .slice(0, 2);
    }

    // The author whose initials + chosen colors this pin represents: the
    // comment's createdBy when it's a saved comment, otherwise the current user
    // for an in-progress pin. Resolved colors let a normal (non-AI,
    // non-required) pin reflect the colors that user picked in Edit Profile,
    // keyed stably on their id, instead of the old hardcoded accent blue.
    const pinAuthor =
      typeof comment?.createdBy === "object" && comment?.createdBy
        ? comment.createdBy
        : { userID: user?.userID, name: user?.name };
    const authorColors = resolveAvatarColors(pinAuthor);
    // A normal user pin is one that shows initials and carries no required/AI
    // semantic color. Those two keep their dedicated colors (red / gold).
    const isUserColoredPin = !isRequired && !isAI;

    function renderAvatar() {
      if (!comment || !isAI) {
        const name =
          !comment && user?.name
            ? user.name
            : typeof comment?.createdBy === "object" && comment.createdBy?.name
              ? comment.createdBy.name
              : user?.name;
        return (
          <Text
            size="1"
            weight="bold"
            className={cx("leading-none", {
              "text-base-white": !isUserColoredPin,
            })}
            style={
              isUserColoredPin ? { color: authorColors.textColor } : undefined
            }
          >
            {getInitials(name)}
          </Text>
        );
      }
      const Icon = isRequired ? Prohibit : Warning;
      return <Icon size={14} weight="fill" className="text-base-white" />;
    }

    const bgColor = isRequired
      ? "bg-puntt-red-9"
      : isAI
        ? "bg-puntt-gold-10"
        : "";
    const shadowColor = isRequired
      ? "rgb(var(--puntt-red-9))"
      : isAI
        ? "rgb(var(--puntt-gold-10))"
        : authorColors.backgroundColor;

    const { bodySize, bounds, tipLocalX, tipLocalY } =
      getCanvasPinAvatarLayout(CANVAS_PIN_SIZE_PX);
    const hitClipPath = getCanvasPinHitClipPath(bodySize);

    const handlePinPointerDown = (
      e: React.PointerEvent<HTMLDivElement>,
      hitRect: DOMRect,
    ) => {
      if (
        !isPointInCanvasPinHitBounds(
          e.clientX - hitRect.left,
          e.clientY - hitRect.top,
          bodySize,
        )
      ) {
        return;
      }

      stopEventPropagation(e);
      this.editor.setSelectedShapes([
        shape.id,
        shape.meta.linkedCommentId as TLShapeId,
      ]);
      if (!isNil(comment?._id)) {
        dispatch(setActiveCommentId(comment._id));
      }
    };

    return (
      <HTMLContainer
        style={{
          transform: `scale(${1 / zoomLevel})`,
          position: "relative",
          background: "transparent",
          border: "none",
          width: bounds.width,
          height: bounds.height,
          overflow: "visible",
        }}
      >
        {/* Invisible teardrop hit target — geometry only, no visual change. */}
        <div
          aria-hidden="true"
          data-testid="canvas-comment-pin-hit"
          className="absolute [pointer-events:all]"
          style={{
            left: 0,
            top: 0,
            width: bounds.width,
            height: bounds.height,
            clipPath: hitClipPath,
          }}
          onPointerDown={(e) => {
            handlePinPointerDown(e, e.currentTarget.getBoundingClientRect());
          }}
        />
        {/* Original pin markup — hover scale comes from assets.ts `.comment-pin:hover`. */}
        <div
          className="comment-pin relative max-w-max"
          data-testid="canvas-comment-pin"
          aria-label="Open comment"
          style={{
            position: "absolute",
            left: tipLocalX,
            top: tipLocalY - bodySize,
          }}
          onPointerDown={(e) => {
            const hitLayer = e.currentTarget.parentElement?.querySelector(
              '[data-testid="canvas-comment-pin-hit"]',
            ) as HTMLElement | null;
            handlePinPointerDown(
              e,
              hitLayer?.getBoundingClientRect() ??
                e.currentTarget.getBoundingClientRect(),
            );
          }}
        >
          <div
            className={cx(
              "comment-pin flex size-[25px] items-center justify-center rounded-[20px_20px_20px_0]",
              bgColor,
            )}
            style={{
              transform: `rotate(-45deg)${highlightedCommentId === comment?._id ? " scale(1.25)" : ""}`,
              transformOrigin: "0% 100%",
              boxShadow: `0 0 0 2px ${shadowColor}, 0 1px 3px 2px rgba(0, 0, 0, 0.12), 0 1px 2px 2px rgba(0, 0, 0, 0.24)`,
              ...(isUserColoredPin
                ? { backgroundColor: authorColors.backgroundColor }
                : {}),
            }}
          >
            <div style={{ transform: "rotate(45deg)" }}>{renderAvatar()}</div>
          </div>
        </div>
      </HTMLContainer>
    );
  };

  indicator = (_shape: CommentShape) => null;
}
